/**
 * Super admin home — the platform at a glance.
 *
 * On "revenue": this database has no payments/transactions table, so lifetime
 * billed revenue exists only in Stripe. The card therefore shows MRR (every
 * active subscription at its tier's monthly price), which is derivable here and
 * is the number that answers "what are we earning right now". Paid listing
 * unlocks ARE recorded locally, so they get their own figure rather than being
 * folded into a single misleading "total".
 */
import { useEffect, useState } from "react";
import { Building2, CreditCard, LayoutGrid, Loader2, UserCheck, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { PageHero } from "@/components/interior-page";

import { superAdminApi } from "./session";

interface Stats {
  total_buyers: number;
  total_bl_advisors: number;
  total_listings: number;
  total_orgs: number;
  mrr: number;
  active_subscriptions: number;
  unlock_revenue: number;
}

const num = (n: number) => n.toLocaleString();
const usd = (n: number) =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function StatCard({
  label, value, sub, icon: Icon,
}: { label: string; value: string; sub?: string; icon: LucideIcon }) {
  return (
    <div className="rounded-2xl border border-border/80 bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </div>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-3xl leading-none text-foreground">{value}</div>
      {sub && <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function SuperAdminHomePage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    superAdminApi
      .get<Stats>("/super-admin/stats")
      .then((s) => { if (!stale) setStats(s); })
      .catch((e) => { if (!stale) setError(e instanceof Error ? e.message : "Could not load platform stats."); });
    return () => { stale = true; };
  }, []);

  return (
    <main className="w-full space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <PageHero
        eyebrow="Super Admin"
        icon={Building2}
        title="Platform"
        titleAccent="Overview"
        description="Every organization, buyer, advisor and listing on BusinessLocating, in one place."
      />

      {error && (
        <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600">
          {error}
        </p>
      )}

      {!stats && !error ? (
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading platform stats…
        </div>
      ) : stats ? (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <StatCard label="Total Buyers" value={num(stats.total_buyers)} icon={Users} />
            <StatCard label="BL Advisors" value={num(stats.total_bl_advisors)} icon={UserCheck} />
            <StatCard label="Total Listings" value={num(stats.total_listings)} sub="Excludes deleted" icon={LayoutGrid} />
            <StatCard
              label="Revenue (MRR)"
              value={usd(stats.mrr)}
              sub={`${num(stats.active_subscriptions)} active subscription${stats.active_subscriptions === 1 ? "" : "s"}`}
              icon={CreditCard}
            />
            <StatCard label="Organizations" value={num(stats.total_orgs)} sub="Active" icon={Building2} />
          </section>

          <section className="rounded-2xl border border-border/70 bg-muted/30 p-5">
            <div className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              How revenue is counted
            </div>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
              <strong className="text-foreground">MRR</strong> is every active subscription priced at its
              tier&apos;s monthly amount — what the platform earns per month as of right now. Lifetime billed
              revenue is not shown because it is not stored here; Stripe is the record for that.
              Paid listing unlocks are recorded locally and total{" "}
              <strong className="text-foreground">{usd(stats.unlock_revenue)}</strong> all time.
            </p>
          </section>
        </>
      ) : null}
    </main>
  );
}
